import React, { lazy, Suspense } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { CombinedAuthProvider, useAuth } from "./contexts/CombinedAuthContext";
import { PlansProvider, usePlans } from "./contexts/PlansContext";
import { AdminProvider } from "./contexts/AdminContext";
import Header from "./components/Header";
import HomePage from "./pages/HomePage";
import BusinessListings from "./pages/BusinessListings";
import TokensAndProducts from "./pages/TokensAndProducts";
import PromotionCart from "./pages/PromotionCart";
import RoommateFinder from "./pages/RoommateFinder";
import { PerkGate } from "./components/PerkGate";
import AccountSettings from "./pages/AccountSettings";
import HelpSupport from "./pages/HelpSupport";
import NotFoundPage from "./pages/NotFoundPage";
import LoginCallback from "./components/LoginCallback";
import TokenLogin from "./components/TokenLogin";
import TokenRedirect from "./components/TokenRedirect";
import LoadingScreen from "./components/LoadingScreen";
import Footer from "./components/Footer";
import EditBusinessListing from "./components/EditBusinessListing";
import BusinessDashboard from "./pages/BusinessDashboard";
import { ErrorBoundary } from "./components/ui/ErrorBoundary";
import { ToastProvider, ToastContainer } from "./components/ui/Toast";
import { ConfirmProvider } from "./components/ui/ConfirmDialog";
import { BookmarksProvider } from "./components/Bookmarks";
import { SignupGateProvider } from "./components/auth/SignupGate";
import ConsentBanner from "./components/legal/ConsentBanner";
import { OfflineBanner } from "./components/ui/OfflineBanner";
import { PageProgress } from "./components/ui/ProgressIndicator";
import { SkipLinks } from "./components/ui/SkipLinks";
import { MobileBottomNav } from "./components/ui/MobileNav";
import SiteBeacon from "./components/SiteBeacon";
import BlockScreen from "./components/BlockScreen";
import { useRouteProgress } from "./hooks";
import "./App.css";

// V2 marketing pages (Phase 2 — lazy loaded)
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Doubloons = lazy(() => import("./pages/Doubloons"));
const ForBusinesses = lazy(() => import("./pages/ForBusinesses"));
const TravelerPasses = lazy(() => import("./pages/TravelerPasses"));
const SignupCTA = lazy(() => import("./pages/SignupCTA"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const PrivacyControls = lazy(() => import("./pages/PrivacyControls"));
const PirateMap = lazy(() => import("./pages/PirateMap"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const Contact = lazy(() => import("./pages/Contact"));
const ContactThankYou = lazy(() => import("./pages/ContactThankYou"));
const Properties = lazy(() => import("./pages/Properties"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const TreasureChest = lazy(() => import("./pages/TreasureChest"));
const RealEstateListingsAdmin = lazy(() => import("./components/admin/RealEstateListingsAdmin"));
const PropertyInquiriesAdmin = lazy(() => import("./components/admin/PropertyInquiriesAdmin"));
const BrokerPartnershipsAdmin = lazy(() => import("./components/admin/BrokerPartnershipsAdmin"));
const BookingsHealth = lazy(() => import("./components/admin/BookingsHealth"));
const MembershipMigration = lazy(() => import("./components/admin/MembershipMigration"));
const ForBrokers = lazy(() => import("./pages/ForBrokers"));

// Admin Components (lazy loaded for code splitting)
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./components/admin/AdminDashboard"));
const EmailMonitor = lazy(() => import("./components/admin/EmailMonitor"));
const AlertsManager = lazy(() => import("./components/admin/AlertsManager"));
const UserManagement = lazy(() => import("./components/admin/UserManagement"));
const ListingsAdmin = lazy(() => import("./components/admin/ListingsAdmin"));
const TokenEconomy = lazy(() => import("./components/admin/TokenEconomy"));
const TokenPacksAdmin = lazy(() => import("./components/admin/TokenPacksAdmin"));
const Reports = lazy(() => import("./components/admin/Reports"));

// Staff portal (separate Firebase Auth tenancy)
const StaffAuthProvider = lazy(() =>
  import("./contexts/StaffAuthContext").then((m) => ({ default: m.StaffAuthProvider })));
const StaffLayout = lazy(() => import("./pages/staff/StaffLayout"));
const StaffLogin = lazy(() => import("./pages/staff/StaffLogin"));
const StaffForgotPassword = lazy(() => import("./pages/staff/StaffForgotPassword"));
const StaffFirstLoginWizard = lazy(() => import("./pages/staff/StaffFirstLoginWizard"));
const StaffAccount = lazy(() => import("./pages/staff/StaffAccount"));
const StaffRedemptionPage = lazy(() => import("./pages/staff/StaffRedemptionPage"));
const StaffGuard = lazy(() => import("./pages/staff/StaffGuard"));
const AdminSettings = lazy(() => import("./components/admin/AdminSettings"));
const TokenVelocity = lazy(() => import("./components/admin/TokenVelocity"));
const TokenPackRevenue = lazy(() => import("./components/admin/TokenPackRevenue"));
const MemberLTV = lazy(() => import("./components/admin/MemberLTV"));
const MemberSegmentation = lazy(() => import("./components/admin/MemberSegmentation"));
const BusinessRevenue = lazy(() => import("./components/admin/BusinessRevenue"));
const CampaignManager = lazy(() => import("./components/admin/CampaignManager"));
const MapEditor = lazy(() => import("./components/admin/MapEditor"));
const CampaignPerformance = lazy(() => import("./components/admin/CampaignPerformance"));
const TokenBurnPrevention = lazy(() => import("./components/admin/TokenBurnPrevention"));
const RevenueAttribution = lazy(() => import("./components/admin/RevenueAttribution"));
const RedemptionForecasting = lazy(() => import("./components/admin/RedemptionForecasting"));
const RoyaltyEngine = lazy(() => import("./components/admin/RoyaltyEngine"));
const InventoryPressureForecasting = lazy(() => import("./components/admin/InventoryPressureForecasting"));
const ChurnRiskQueue = lazy(() => import("./components/admin/ChurnRiskQueue"));

// New Admin Components (Tier 3-4)
const MemberEngagement = lazy(() => import("./components/admin/MemberEngagement"));
const ListingPerformance = lazy(() => import("./components/admin/ListingPerformance"));
const RedemptionOperations = lazy(() => import("./components/admin/RedemptionOperations"));
const ModerationQueue = lazy(() => import("./components/admin/ModerationQueue"));
const BusinessOwnerEngagement = lazy(() => import("./components/admin/BusinessOwnerEngagement"));
const ScheduledReports = lazy(() => import("./components/admin/ScheduledReports"));
const NotificationCenter = lazy(() => import("./components/admin/NotificationCenter"));
const EmailQueueManager = lazy(() => import("./components/admin/EmailQueueManager"));
const EmailHealthDashboard = lazy(() => import("./components/admin/EmailHealthDashboard"));
const EmailTemplateManager = lazy(() => import("./components/admin/EmailTemplateManager"));
const AuditLogViewer = lazy(() => import("./components/admin/AuditLogViewer"));
const SiteTraffic = lazy(() => import("./components/admin/SiteTraffic"));
const ErrorMonitor = lazy(() => import("./components/admin/ErrorMonitor"));
const PerformanceDashboard = lazy(() => import("./components/admin/PerformanceDashboard"));
const SystemHealth = lazy(() => import("./components/admin/SystemHealth"));
const AdminLogin = lazy(() => import("./components/admin/AdminLogin"));
const AdminAuthSettings = lazy(() => import("./components/admin/AdminAuthSettings"));

// Protected route component — requires authentication only.
// Subscription gating is handled per-page with CTA dialogs.
const ProtectedRoute = ({ element }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // During auth bootstrap, avoid redirecting deep links prematurely.
  if (loading) {
    return <LoadingScreen />;
  }

  // If not authenticated, redirect to the welcome page.
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
        replace
      />
    );
  }

  return element;
};

// Business-only protected route — requires an active Business Owner subscription.
const BusinessProtectedRoute = ({ element }) => {
  const { isAuthenticated, loading } = useAuth();
  // Free Crew Listing members belong in the dashboard too — gate on any
  // business tier, not just the paid ones.
  const { isBusinessMember } = usePlans();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
        replace
      />
    );
  }

  if (!isBusinessMember()) {
    return <Navigate to="/business-listings" replace />;
  }

  return element;
};

const StaffRouteFallback = () => {
  const location = useLocation();
  return (
    <Navigate
      to="/staff/login"
      state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      replace
    />
  );
};

// Main application component that uses auth context
const AppContent = () => {
  const { loading } = useAuth();
  const location = useLocation();
  const { isLoading: isRouteLoading, progress: routeProgress } = useRouteProgress();
  const [accessBlocked, setAccessBlocked] = React.useState(false);

  // Check if we're on a special auth route
  const isAuthRoute =
    location.pathname === "/login-callback" ||
    location.pathname === "/token-login";

  if (loading && isAuthRoute) {
    return <LoadingScreen />;
  }

  // Security blocklist hit — replace the whole app with the block screen.
  if (accessBlocked) {
    return <BlockScreen />;
  }

  return (
    <div className="app-container">
      <SkipLinks />
      <PageProgress loading={isRouteLoading} progress={routeProgress} />
      <SiteBeacon onBlocked={() => setAccessBlocked(true)} />
      <TokenRedirect />
      <Routes>
        <Route path="/login-callback" element={<LoginCallback />} />
        <Route path="/token-login" element={<TokenLogin />} />
        <Route path="/token-redirect" element={<TokenRedirect />} />

        {/* Staff portal — isolated Firebase Auth tenancy, no header/footer */}
        <Route
          path="/staff/*"
          element={
            <Suspense fallback={<LoadingScreen message="Loading staff portal..." />}>
              <StaffAuthProvider>
                <StaffLayout />
              </StaffAuthProvider>
            </Suspense>
          }
        >
          <Route path="login" element={<StaffLogin />} />
          <Route path="forgot" element={<StaffForgotPassword />} />
          <Route element={<StaffGuard />}>
            <Route path="setup" element={<StaffFirstLoginWizard />} />
            <Route path="account" element={<StaffAccount />} />
            <Route path="redeem" element={<StaffRedemptionPage />} />
            <Route index element={<Navigate to="redeem?mode=scan" replace />} />
          </Route>
          <Route path="*" element={<StaffRouteFallback />} />
        </Route>

        {/* Admin Login (separate route - no layout) */}
        <Route
          path="/admin/login"
          element={
            <Suspense fallback={<LoadingScreen message="Loading..." />}>
              <AdminLogin />
            </Suspense>
          }
        />

        {/* Admin routes (lazy loaded) */}
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<LoadingScreen message="Loading Admin..." />}>
              <AdminLayout />
            </Suspense>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="email" element={<EmailMonitor />} />
          <Route path="email-queue" element={<EmailQueueManager />} />
          <Route path="email-health" element={<EmailHealthDashboard />} />
          <Route path="email-templates" element={<EmailTemplateManager />} />
          <Route path="alerts" element={<AlertsManager />} />
          <Route path="notifications" element={<NotificationCenter />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="listings" element={<ListingsAdmin />} />
          <Route path="moderation" element={<ModerationQueue />} />
          <Route path="tokens" element={<TokenEconomy />} />
          <Route path="token-velocity" element={<TokenVelocity />} />
          <Route path="token-revenue" element={<TokenPackRevenue />} />
          <Route path="member-ltv" element={<MemberLTV />} />
          <Route path="member-segmentation" element={<MemberSegmentation />} />
          <Route path="business-revenue" element={<BusinessRevenue />} />
          <Route path="campaigns" element={<CampaignManager />} />
          <Route path="campaigns/performance" element={<CampaignPerformance />} />
          <Route path="map-editor" element={<MapEditor />} />
          <Route path="real-estate" element={<RealEstateListingsAdmin />} />
          <Route path="property-inquiries" element={<PropertyInquiriesAdmin />} />
          <Route path="broker-partnerships" element={<BrokerPartnershipsAdmin />} />
          <Route path="bookings-health" element={<BookingsHealth />} />
          <Route path="membership-migration" element={<MembershipMigration />} />
          <Route path="token-burn-prevention" element={<TokenBurnPrevention />} />
          <Route path="revenue-attribution" element={<RevenueAttribution />} />
          <Route path="royalty" element={<RoyaltyEngine />} />
          <Route path="redemption-forecasting" element={<RedemptionForecasting />} />
          <Route path="inventory-pressure" element={<InventoryPressureForecasting />} />
          <Route path="churn-risk-queue" element={<ChurnRiskQueue />} />
          <Route path="token-packs" element={<TokenPacksAdmin />} />
          <Route path="reports" element={<Reports />} />
          <Route path="reports/member-engagement" element={<MemberEngagement />} />
          <Route path="reports/listing-performance" element={<ListingPerformance />} />
          <Route path="reports/redemptions" element={<RedemptionOperations />} />
          <Route path="reports/business-engagement" element={<BusinessOwnerEngagement />} />
          <Route path="reports/scheduled" element={<ScheduledReports />} />
          <Route path="audit-log" element={<AuditLogViewer />} />
          <Route path="site-traffic" element={<SiteTraffic />} />
          <Route path="error-monitor" element={<ErrorMonitor />} />
          <Route path="performance" element={<PerformanceDashboard />} />
          <Route path="system-health" element={<SystemHealth />} />
          <Route path="security" element={<AdminAuthSettings />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Main routes with header and footer */}
        <Route
          path="/*"
          element={
            <>
              <Header />
              <main id="main-content" className="main">
                <Routes>
                  {/* Fixed route patterns to properly match the URL */}
                  <Route index element={<HomePage />} />

                  {/* <Route
                    path="business-listings/*"
                    element={<BusinessListings />}
                  /> */}
                  <Route
                    path="business-listings"
                    element={<BusinessListings />}
                  />
                  <Route
                    path="business-listings/:listingId"
                    element={<BusinessListings />}
                  />
                  <Route
                    path="edit-business-listing/:id"
                    element={<BusinessProtectedRoute element={<EditBusinessListing />} />}
                  />

                  <Route
                    path="business-dashboard"
                    element={<BusinessProtectedRoute element={<BusinessDashboard />} />}
                  />

                  <Route
                    path="tokens/*"
                    element={<ProtectedRoute element={<TokensAndProducts />} />}
                  />

                  <Route
                    path="promotions/cart"
                    element={<ProtectedRoute element={<PromotionCart />} />}
                  />

                  <Route
                    path="roommate-finder/*"
                    element={
                      <ProtectedRoute
                        element={
                          <PerkGate perk="roommateJobs" loadingFallback={<LoadingScreen />}>
                            <RoommateFinder />
                          </PerkGate>
                        }
                      />
                    }
                  />

                  <Route
                    path="account/*"
                    element={<ProtectedRoute element={<AccountSettings />} />}
                  />

                  <Route path="support/*" element={<HelpSupport />} />

                  {/* Legal pages (public) */}
                  <Route
                    path="privacy"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <Privacy />
                      </Suspense>
                    }
                  />
                  <Route
                    path="terms"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <Terms />
                      </Suspense>
                    }
                  />
                  <Route
                    path="cookies"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <CookiePolicy />
                      </Suspense>
                    }
                  />
                  <Route
                    path="account/privacy"
                    element={
                      <ProtectedRoute
                        element={
                          <Suspense fallback={<LoadingScreen />}>
                            <PrivacyControls />
                          </Suspense>
                        }
                      />
                    }
                  />

                  {/* V2 marketing routes (Phase 2) */}
                  <Route
                    path="signup"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <SignupCTA />
                      </Suspense>
                    }
                  />
                  <Route
                    path="how-it-works"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <HowItWorks />
                      </Suspense>
                    }
                  />
                  <Route
                    path="doubloons"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <Doubloons />
                      </Suspense>
                    }
                  />
                  <Route
                    path="for-businesses"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <ForBusinesses />
                      </Suspense>
                    }
                  />
                  <Route
                    path="traveler-passes"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <TravelerPasses />
                      </Suspense>
                    }
                  />
                  <Route
                    path="pirate-map"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <PirateMap />
                      </Suspense>
                    }
                  />
                  <Route
                    path="events"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <Events />
                      </Suspense>
                    }
                  />
                  <Route
                    path="events/:eventId"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <EventDetail />
                      </Suspense>
                    }
                  />
                  <Route
                    path="account/bookings"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <MyBookings />
                      </Suspense>
                    }
                  />
                  <Route
                    path="contact"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <Contact />
                      </Suspense>
                    }
                  />
                  <Route
                    path="contact/thank-you"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <ContactThankYou />
                      </Suspense>
                    }
                  />
                  <Route
                    path="properties"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <Properties />
                      </Suspense>
                    }
                  />
                  <Route
                    path="properties/:slug"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <PropertyDetail />
                      </Suspense>
                    }
                  />
                  <Route
                    path="for-brokers"
                    element={
                      <Suspense fallback={<LoadingScreen />}>
                        <ForBrokers />
                      </Suspense>
                    }
                  />
                  <Route
                    path="treasure-chest"
                    element={
                      <ProtectedRoute element={
                        <Suspense fallback={<LoadingScreen />}>
                          <TreasureChest />
                        </Suspense>
                      } />
                    }
                  />

                  {/* Legacy routes - redirect to new structure */}
                  <Route
                    path="hospitality"
                    element={
                      <Navigate
                        to="/business-listings?filter=hospitality"
                        replace
                      />
                    }
                  />
                  <Route
                    path="consulting"
                    element={
                      <Navigate
                        to="/business-listings?filter=consulting"
                        replace
                      />
                    }
                  />
                  <Route
                    path="pirates-guild"
                    element={
                      <Navigate to="/business-listings?filter=guild" replace />
                    }
                  />

                  {/* Catch-all for unknown routes */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </main>
              <Footer />
              <MobileBottomNav />
            </>
          }
        />
      </Routes>
    </div>
  );
};

// Wrapper component that provides auth context
function App() {
  return (
    <ErrorBoundary>
      <CombinedAuthProvider>
        <PlansProvider>
          <AdminProvider>
            <ToastProvider>
              <ConfirmProvider>
                <BookmarksProvider>
                  <SignupGateProvider>
                    <OfflineBanner />
                    <AppContent />
                    <ToastContainer />
                    <ConsentBanner />
                  </SignupGateProvider>
                </BookmarksProvider>
              </ConfirmProvider>
            </ToastProvider>
          </AdminProvider>
        </PlansProvider>
      </CombinedAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
